(() => {
  'use strict';
  if (window.__wubOptimisticAtcLoaded) return;
  window.__wubOptimisticAtcLoaded = true;

  const CONTROL = '[data-wub-drawer-quantity-action],[data-wub-drawer-remove],[data-wub-drawer-quantity-input],[data-wub-checkout]';
  const HISTORY_KEY = 'wubCartDrawerOpen';
  let active = null;

  const now = () => performance?.now?.() ?? Date.now();
  const drawerSection = () => document.querySelector('[data-wub-cart-drawer-section]');
  const drawer = () => document.getElementById('WubCartDrawer');
  const isOpen = () => drawer()?.classList.contains('is-open') === true;
  const lang = () => String(document.documentElement.lang || 'nl').toLowerCase().split('-')[0];
  const text = (type) => ({
    nl: ['Wordt toegevoegd…', 'Toevoegen mislukt. Probeer het opnieuw.'],
    en: ['Adding to cart…', 'Could not add this item. Please try again.'],
    de: ['Wird hinzugefügt…', 'Hinzufügen fehlgeschlagen. Bitte versuche es erneut.'],
    fr: ['Ajout en cours…', 'Impossible d’ajouter cet article. Réessaie.']
  }[lang()] || ['Wordt toegevoegd…', 'Toevoegen mislukt. Probeer het opnieuw.'])[type === 'failed' ? 1 : 0];

  function status(state, copy = '') {
    const root = drawer();
    const node = document.querySelector('[data-wub-cart-status]');
    if (root) {
      if (state) root.dataset.wubAddState = state;
      else delete root.dataset.wubAddState;
      root.toggleAttribute('aria-busy', state === 'pending');
    }
    if (!node) return;
    node.setAttribute('role', state === 'error' ? 'alert' : 'status');
    node.setAttribute('aria-live', 'polite');
    node.setAttribute('aria-atomic', 'true');
    node.textContent = copy;
    node.hidden = !copy;
    node.style.color = state === 'error' ? 'var(--wub-color-sale,#d83f3f)' : state === 'pending' ? 'var(--wub-color-muted,#64717c)' : '';
    node.style.padding = state === 'pending' || state === 'error' ? '10px var(--wub-gutter-mobile,16px) 0' : '';
    node.style.textTransform = state === 'pending' || state === 'error' ? 'none' : '';
    node.style.letterSpacing = state === 'pending' || state === 'error' ? '0' : '';
  }

  function directOpen(trigger) {
    const root = drawer();
    if (!root) return false;
    const wasOpen = isOpen();
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('wub-commerce-locked');
    if (!wasOpen) {
      requestAnimationFrame(() => root.querySelector('.wub-cart-drawer__panel')?.focus?.({ preventScroll: true }));
      document.dispatchEvent(new CustomEvent('wub:cart-drawer-opened', { detail: { source: 'optimistic-atc', trigger, optimistic: true } }));
    }
    return true;
  }

  function openPending(trigger) {
    status('pending', text('pending'));
    const opened = window.WUBCommerce?.openDrawer
      ? window.WUBCommerce.openDrawer({ added: false, trigger }) === true
      : directOpen(trigger);
    if (opened) document.dispatchEvent(new CustomEvent('wub:cart-drawer-optimistic-opened', { detail: { trigger } }));
    return opened;
  }

  function removeHistoryMarker() {
    const current = history.state;
    if (!current || typeof current !== 'object' || current[HISTORY_KEY] !== true) return;
    const next = { ...current };
    delete next[HISTORY_KEY];
    history.replaceState(next, '', location.href);
  }

  function restoreDrawerFastPatch(record) {
    const patch = record?.drawerFastPatch;
    if (!patch) return;
    const { root, ownDescriptor, replacement } = patch;
    try {
      if (root?.replaceWith === replacement) {
        if (ownDescriptor) Object.defineProperty(root, 'replaceWith', ownDescriptor);
        else delete root.replaceWith;
      }
    } catch (_) {}
    record.drawerFastPatch = null;
  }

  function installDrawerFastPatch(record) {
    const root = drawerSection();
    if (!root || typeof root.replaceWith !== 'function') return;

    const ownDescriptor = Object.getOwnPropertyDescriptor(root, 'replaceWith');
    const nativeReplace = root.replaceWith.bind(root);

    const replacement = function wubOptimisticDrawerReplace(...nodes) {
      const next = nodes.length === 1 ? nodes[0] : null;
      const currentDrawer = root.querySelector('#WubCartDrawer');
      const nextDrawer = next?.querySelector?.('#WubCartDrawer');
      const currentBody = currentDrawer?.querySelector('[data-wub-cart-drawer-body]');
      const nextBody = nextDrawer?.querySelector('[data-wub-cart-drawer-body]');

      if (
        active !== record ||
        record.done ||
        !root.isConnected ||
        !next?.matches?.('[data-wub-cart-drawer-section]') ||
        !currentDrawer?.classList.contains('is-open') ||
        currentDrawer.dataset.wubAddState !== 'pending' ||
        !currentBody ||
        !nextBody
      ) {
        return nativeReplace(...nodes);
      }

      Array.from(next.attributes || []).forEach((attribute) => {
        if (attribute.name.startsWith('data-')) root.setAttribute(attribute.name, attribute.value);
      });

      const currentFooter = currentDrawer.querySelector('.wub-cart-drawer__footer');
      const nextFooter = nextDrawer.querySelector('.wub-cart-drawer__footer');

      currentBody.replaceWith(nextBody);
      if (currentFooter && nextFooter) currentFooter.replaceWith(nextFooter);
      else if (currentFooter) currentFooter.remove();
      else if (nextFooter) nextBody.insertAdjacentElement('afterend', nextFooter);

      record.fastPatchUsed = true;
      record.fastPatchedAt = now();
      document.dispatchEvent(new CustomEvent('wub:cart-drawer-fast-patched', {
        detail: {
          patchedAt: record.fastPatchedAt,
          item_count: Number(root.dataset.wubCartItemCount || 0)
        }
      }));
      return undefined;
    };

    try {
      Object.defineProperty(root, 'replaceWith', {
        configurable: true,
        writable: true,
        value: replacement
      });
      record.drawerFastPatch = { root, ownDescriptor, replacement };
    } catch (_) {}
  }

  function cleanup(record) {
    record?.observer?.disconnect();
    clearTimeout(record?.timeout);
    restoreDrawerFastPatch(record);
    if (active === record) active = null;
  }

  function fail(reason) {
    const record = active;
    if (!record || record.done) return;
    record.done = true;
    record.failedAt = now();
    if (isOpen()) status('error', text('failed'));
    document.dispatchEvent(new CustomEvent('wub:cart-add-failed', { detail: { reason, failedAt: record.failedAt } }));
    cleanup(record);
  }

  function watch(record) {
    const button = record.submitter;
    if (!button || !window.MutationObserver) return;
    let sawBusy = button.getAttribute('aria-busy') === 'true';
    record.observer = new MutationObserver(() => {
      if (button.getAttribute('aria-busy') === 'true') { sawBusy = true; return; }
      if (!sawBusy) return;
      queueMicrotask(() => {
        if (active !== record || record.done || button.getAttribute('aria-busy') === 'true' || !window.WUBCommerce) return;
        fail('request-finished-without-success');
      });
    });
    record.observer.observe(button, { attributes: true, attributeFilter: ['aria-busy'] });
  }

  function begin(form, submitter) {
    if (active) return false;
    const record = {
      form,
      submitter,
      startedAt: now(),
      drawerAt: null,
      fastPatchedAt: null,
      fastPatchUsed: false,
      closed: false,
      done: false,
      awaitingRuntimeReplay: !window.WUBCommerce?.openDrawer,
      observer: null,
      timeout: null,
      drawerFastPatch: null
    };
    active = record;
    if (openPending(submitter)) record.drawerAt = now();
    installDrawerFastPatch(record);
    watch(record);
    record.timeout = setTimeout(() => fail('timeout'), 15000);
    document.dispatchEvent(new CustomEvent('wub:cart-add-pending', { detail: { startedAt: record.startedAt, drawerAt: record.drawerAt } }));
    return true;
  }

  function confirm() {
    const record = active;
    if (!record || record.done) return;
    record.done = true;
    record.confirmedAt = now();
    const closed = record.closed || !isOpen();
    if (!closed) {
      const label = drawerSection()?.dataset.addedLabel || '';
      status(label ? 'confirmed' : '', label);
    }
    document.dispatchEvent(new CustomEvent('wub:cart-add-confirmed', {
      detail: {
        startedAt: record.startedAt,
        drawerAt: record.drawerAt,
        fastPatchedAt: record.fastPatchedAt,
        fastPatchUsed: record.fastPatchUsed,
        confirmedAt: record.confirmedAt,
        drawerClosed: closed
      }
    }));
    cleanup(record);
    if (closed) queueMicrotask(() => {
      if (isOpen()) window.WUBCommerce?.closeDrawer?.({ restoreFocus: false });
      removeHistoryMarker();
    });
  }

  function productForm(form) {
    if (!(form instanceof HTMLFormElement) || !(form.getAttribute('action') || '').includes('/cart/add')) return false;
    return Boolean(form.querySelector('[data-wub-main-atc-button],[data-wub-sticky-atc-button]'));
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!productForm(form) || !form.checkValidity()) return;
    if (active) {
      const isRuntimeReplay = active.form === form && active.awaitingRuntimeReplay && Boolean(window.WUBCommerce?.openDrawer);
      if (isRuntimeReplay) {
        active.awaitingRuntimeReplay = false;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const submitter = event.submitter?.form === form ? event.submitter : form.querySelector('[data-wub-main-atc-button],[data-wub-sticky-atc-button],button[type="submit"]');
    if (!submitter?.disabled) begin(form, submitter || null);
  }, true);

  document.addEventListener('cart:item-added', confirm);

  document.addEventListener('click', (event) => {
    if (!active) return;
    if (event.target.closest?.('[data-wub-cart-close]')) active.closed = true;
    if (!event.target.closest?.(CONTROL)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('change', (event) => {
    if (!active || !event.target.closest?.('[data-wub-drawer-quantity-input]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
  document.addEventListener('keydown', (event) => { if (active && event.key === 'Escape' && isOpen()) active.closed = true; }, true);
  addEventListener('popstate', () => { if (active) active.closed = true; });
})();
